// MPS-803 protocol decoder. Turns the printer data stream into lines of
// positioned items (an intermediate representation shared by the text, PNG and
// PDF renderers). Behaviour follows VICE's printerdrv/drv-mps803.c
// (mps_engine_putc), GPL, plus the MPS-803 user manual for what VICE leaves
// open (repeat count 0 = 256).
//
// IR, one object per printed line:
//   { items: [Item], bitmap: boolean, gapRows: number }
//   Item = { kind: 'char', x, code, set: 'graphics'|'business', reverse, wide }
//        | { kind: 'bits', x, value }          value: 7 pins, bit0 = top pin
// x is in dots from the left margin (6 dots per normal character, 12 when
// wide). gapRows is the extra paper feed after the line in dot rows (3 in text
// mode, 0 in bit image mode: 6 lpi text vs 9 lpi graphics, as VICE does).

export const DOTS_PER_LINE = 480; // 80 columns x 6 dots
export const CHAR_WIDTH = 6;

const CR = 13;
const isDigit = (b) => b >= 0x30 && b <= 0x39;

function atoi(bytes) {
  let n = 0;
  for (const b of bytes) {
    if (!isDigit(b)) break;
    n = n * 10 + (b - 0x30);
  }
  return n;
}

export class Mps803Decoder {
  // onLine(line) is called whenever a line is printed (CR, LF or auto-wrap).
  constructor({ onLine } = {}) {
    this.onLine = onLine ?? (() => {});
    this.sa = 0; // current secondary address (channel 0..15)
    this.lastSa = -1;
    this.resetState();
  }

  resetState() {
    this.items = [];
    this.pos = 0; // head position in dots
    this.reverse = false;
    this.quoted = false;
    this.wideMode = false;
    this.bitmode = false;
    this.repeat = false; // next byte is a repeat count
    this.repeatN = 0;
    this.esc = false;
    this.tab = 0; // remaining bytes of a tab argument
    this.tabBytes = [];
    this.graphics = true; // character set: true = graphics, false = business
    this.beginLine = true;
  }

  // ---- stream events ----
  listen(sa) {
    this.sa = sa & 0x0f;
    this.dataFrame = (sa & 0xf0) === 0x60;
  }

  unlisten() {
    this.dataFrame = false;
  }

  reset() {
    this.resetState();
    this.lastSa = -1;
  }

  data(bytes) {
    if (!this.dataFrame) return; // OPEN/CLOSE frames carry no print data
    for (const b of bytes) this.putc(b);
  }

  handlers() {
    return {
      onListen: (sa) => this.listen(sa),
      onUnlisten: () => this.unlisten(),
      onData: (d) => this.data(d),
      onReset: () => this.reset(),
    };
  }

  // ---- engine ----
  endLine() {
    const line = { items: this.items, bitmap: this.bitmode, gapRows: this.bitmode ? 0 : 3 };
    this.items = [];
    this.pos = 0;
    this.onLine(line);
  }

  // Items of the line that has not been printed yet (no CR/LF received).
  pending() {
    return { items: this.items, bitmap: this.bitmode, gapRows: this.bitmode ? 0 : 3 };
  }

  printBits(value) {
    const n = this.repeatN || 1;
    this.repeatN = 0;
    for (let i = 0; i < n; i++) {
      if (this.pos >= DOTS_PER_LINE) this.endLine();
      this.items.push({ kind: 'bits', x: this.pos, value: value & 0x7f });
      this.pos++;
    }
  }

  printChar(code, reverse) {
    const width = this.wideMode ? CHAR_WIDTH * 2 : CHAR_WIDTH;
    this.items.push({
      kind: 'char',
      x: this.pos,
      code,
      set: this.graphics ? 'graphics' : 'business',
      reverse,
      wide: this.wideMode,
    });
    this.pos += width;
  }

  putc(c) {
    if (this.tab) {
      this.tabBytes.push(c);
      if (this.tab === 1) {
        this.pos = this.esc ? (this.tabBytes[0] << 8) | this.tabBytes[1] : atoi(this.tabBytes) * CHAR_WIDTH;
        this.esc = false;
      }
      this.tab--;
      return;
    }

    if (this.esc && c !== 16) this.esc = false;

    if (this.repeat) {
      this.repeatN = c === 0 ? 256 : c;
      this.repeat = false;
      return;
    }

    if (this.bitmode && c & 128) {
      this.printBits(c);
      return;
    }

    // The character set follows the secondary address at the start of each
    // line and when it changes (VICE: "not quite clear how this works").
    if (this.beginLine || this.lastSa !== this.sa) this.graphics = this.sa !== 7;
    this.beginLine = false;
    this.lastSa = this.sa;

    if (c === CR) {
      this.quoted = false;
      this.reverse = false;
      this.endLine();
      this.beginLine = true;
      return;
    }

    if (!this.quoted || this.bitmode) {
      switch (c) {
        case 8:
          this.bitmode = true;
          return;
        case 10:
          this.endLine();
          return;
        case 14:
          this.wideMode = true;
          this.bitmode = false;
          return;
        case 15:
          this.wideMode = false;
          this.bitmode = false;
          return;
        case 16:
          this.tab = 2;
          this.tabBytes = [];
          return;
        case 17:
          this.graphics = false;
          return;
        case 145:
          this.graphics = true;
          return;
        case 18:
          this.reverse = true;
          return;
        case 146:
          this.reverse = false;
          return;
        case 26:
          this.repeat = true;
          return;
        case 27:
          this.esc = true;
          return;
        case 2: case 12: case 19: case 29: case 147: case 159:
          return; // unsupported on the 803 (form feed etc.)
        default:
          break; // 160 prints a blank; everything else falls through
      }
    }

    if (this.bitmode) return; // text is ignored in bit image mode

    if (c === 34) this.quoted = !this.quoted;

    const width = this.wideMode ? CHAR_WIDTH * 2 : CHAR_WIDTH;
    if (this.pos + width > DOTS_PER_LINE) this.endLine();

    if (this.quoted) {
      // Inside quotes control codes are made visible as reverse characters.
      if (c <= 0x1f) return this.printChar(c + 0x40, true);
      if (c >= 0x80 && c <= 0x9f) return this.printChar(c - 0x20, true);
    }

    if (c <= 0x1f || (c >= 0x80 && c <= 0x9f)) return; // non-printable: no output
    this.printChar(c, this.reverse);
  }
}
